import { createTheme } from '@mui/material/styles';
import { yomiriTheme } from './theme.ts';
import type { AppearancePreferences } from './appearance.ts';

// Desktop has its own visual system; mobile keeps its existing theme.
export function createDesktopTheme(preferences: AppearancePreferences) {
  const accent = accentColor(preferences.accent);
  const colors = {
    surface: preferences.black ? '#000000' : '#0a0a0d', containerLow: '#121217', container: '#1a1a21',
    containerHigh: '#1e1e26', containerHighest: '#22222b',
    onSurface: '#ffffff', onSurfaceVariant: 'rgba(255,255,255,.72)',
    outline: 'rgba(255,255,255,.14)', outlineVariant: 'rgba(255,255,255,.08)',
    primary: accent, onPrimary: '#ffffff',
    primaryContainer: `${accent}22`, onPrimaryContainer: '#eef1f4',
  };
  return createTheme(yomiriTheme, {
    shape: { borderRadius: 4 },
    palette: {
      primary: { main: colors.primary, contrastText: colors.onPrimary },
      secondary: { main: colors.onSurfaceVariant },
      background: { default: colors.surface, paper: colors.containerLow },
      text: { primary: colors.onSurface, secondary: colors.onSurfaceVariant },
      divider: colors.outlineVariant,
    },
    typography: {
      fontFamily: 'Inter, "Segoe UI", Arial, sans-serif',
      h1: { fontSize: '1.75rem', fontWeight: 650, letterSpacing: '-.02em' },
      h2: { fontSize: '1.35rem', fontWeight: 600 },
      button: { textTransform: 'none', letterSpacing: 0, fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: { styleOverrides: {
        ':root': Object.fromEntries(Object.entries(colors).map(([key, value]) => [`--y-${key}`, value])),
        body: { backgroundColor: colors.surface, color: colors.onSurface },
        ...(!preferences.motion ? { '*, *::before, *::after': { animation: 'none !important', transition: 'none !important' } } : {}),
      } },
      MuiButton: { styleOverrides: {
        root: { borderRadius: 4, boxShadow: 'none', minHeight: 36, paddingInline: 16 },
        contained: { backgroundColor: '#343a41', color: colors.onSurface, border: `1px solid ${colors.outline}`, '&:hover': { backgroundColor: '#40474f', boxShadow: 'none' } },
        outlined: { borderColor: colors.outline, color: colors.onSurface, '&:hover': { backgroundColor: colors.containerHigh, borderColor: colors.onSurfaceVariant } },
      } },
      MuiIconButton: { styleOverrides: { root: { borderRadius: 4, '&:hover': { transform: 'none', backgroundColor: colors.containerHigh } } } },
      MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 4, backgroundColor: colors.containerLow } } },
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none', backgroundColor: colors.containerLow, boxShadow: 'none' } } },
      MuiCard: { styleOverrides: { root: { borderRadius: 4, backgroundColor: colors.containerLow, border: `1px solid ${colors.outlineVariant}` } } },
      MuiChip: { styleOverrides: { root: { borderRadius: 4, backgroundColor: colors.containerHigh } } },
      MuiMenu: { styleOverrides: { paper: { borderRadius: 4, backgroundColor: colors.container, backdropFilter: 'none', border: `1px solid ${colors.outline}`, padding: 4 } } },
      MuiMenuItem: { styleOverrides: { root: { borderRadius: 2, '&.Mui-selected': { backgroundColor: colors.containerHigh, color: colors.onSurface } } } },
      MuiDialog: { styleOverrides: { paper: { borderRadius: 6, backgroundColor: colors.container, border: `1px solid ${colors.outline}` } } },
      MuiAlert: { styleOverrides: { root: { borderRadius: 4 } } },
      MuiTooltip: { styleOverrides: { tooltip: { borderRadius: 4 } } },
    },
  });
}

function accentColor(id: string) {
  return ({ yomiri: '#ff2e63', cherry: '#d85b72', violet: '#9d86d6', mint: '#59bda7', amber: '#d6a556', sky: '#6fa8d8' } as Record<string, string>)[id] || '#ff2e63';
}
